import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Tag, 
  Input, 
  Button, 
  Empty,
  Modal,
  Space,
  // DatePicker
} from 'antd';
import { 
  SearchOutlined, 
  CalendarOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './index.css';

const { Search } = Input;
// const { RangePicker } = DatePicker;

const Archive = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  // 所有标签
  const allTags = Array.from(
    new Set(posts.flatMap(post => post.tags || []))
  );

  // 加载文章
  useEffect(() => {
    const savedPosts = JSON.parse(localStorage.getItem('myBlogPosts') || '[]');
    setPosts(savedPosts);
    setFilteredPosts(savedPosts);
  }, []);

  // 搜索和过滤
  useEffect(() => {
    let result = posts;
    
    // 根据文本搜索
    if (searchText) {
      result = result.filter(post => 
        post.title.toLowerCase().includes(searchText.toLowerCase()) ||
        post.content.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    // 根据标签过滤
    if (selectedTags.length > 0) {
      result = result.filter(post => 
        selectedTags.every(tag => post.tags?.includes(tag))
      );
    }
    
    setFilteredPosts(result);
  }, [searchText, selectedTags, posts]);

  // 查看文章详情
  const handleViewPost = (post) => {
    Modal.info({
      title: post.title,
      width: 800,
      content: (
        <div className="post-detail-modal">
          <div className="post-meta">
            <span className="post-date">
              <CalendarOutlined />
              创建于：{post.createdAt}
            </span>
            {post.updatedAt && (
              <span className="post-update-date">
                更新于：{post.updatedAt}
              </span>
            )}
          </div>
          <div className="post-content">
            {post.content.split('\n').map((line, index) => (
              <p key={index}>{line || <br />}</p>
            ))}
          </div>
          {post.tags && post.tags.length > 0 && (
            <div className="post-tags">
              {post.tags.map(tag => (
                <Tag key={tag} className="detail-tag">{tag}</Tag>
              ))}
            </div>
          )}
        </div>
      ),
      onOk() {},
    });
  };

  // 编辑文章
  const handleEditPost = (post) => {
    // 这里可以跳转到编辑页面，或者使用模态框编辑
    Modal.confirm({
      title: '编辑文章',
      content: '是否要编辑这篇文章？',
      onOk: () => {
        navigate('/write');
      }
    });
  };

  // 删除文章
  const handleDeletePost = (postId) => {
    setPostToDelete(postId);
    setIsDeleteModalVisible(true);
  };

  // 确认删除
  const confirmDelete = () => {
    const updatedPosts = posts.filter(post => post.id !== postToDelete);
    setPosts(updatedPosts);
    localStorage.setItem('myBlogPosts', JSON.stringify(updatedPosts));
    setIsDeleteModalVisible(false);
    setPostToDelete(null);
  };

  // 切换标签选择
  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // 统计信息
  const totalPosts = posts.length;
  const totalWords = posts.reduce((sum, post) => sum + (post.content?.length || 0), 0);
  const earliestDate = posts.length > 0 
    ? posts[posts.length - 1].createdAt 
    : '暂无';

  return (
    <div className="archive-container">
      {/* 统计卡片 */}
      <div className="stats-section">
        <Card className="stats-card">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{totalPosts}</div>
              <div className="stat-label">篇日记</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{Math.floor(totalWords / 100)}</div>
              <div className="stat-label">百字</div>
            </div>
            <div className="stat-item">
              <div className="stat-date">{earliestDate}</div>
              <div className="stat-label">开始记录</div>
            </div>
          </div>
        </Card>
      </div>

      {/* 搜索和过滤 */}
      <div className="filter-section">
        <Card>
          <div className="filter-content">
            <Search
              placeholder="搜索日记..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="search-input"
              allowClear
            />
            
            {allTags.length > 0 && (
              <div className="tags-filter">
                <div className="filter-title">标签筛选：</div>
                <div className="tags-container">
                  {allTags.map(tag => (
                    <Tag
                      key={tag}
                      className={`filter-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </Tag>
                  ))}
                  {selectedTags.length > 0 && (
                    <Button 
                      size="small" 
                      onClick={() => setSelectedTags([])}
                      className="clear-tags-btn"
                    >
                      清除筛选
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* 文章列表 */}
      <Card 
        className="posts-card"
        title={
          <div className="posts-header">
            <BookOutlined />
            <span>我的日记 ({filteredPosts.length} 篇)</span>
          </div>
        }
        extra={
          <Button 
            type="primary" 
            onClick={() => navigate('/write')}
          >
            写新日记
          </Button>
        }
      >
        {filteredPosts.length === 0 ? (
          <Empty 
            description={posts.length === 0 ? "还没有日记，开始写第一篇吧！" : "没有找到符合条件的日记"}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {posts.length === 0 && (
              <Button 
                type="primary" 
                onClick={() => navigate('/write')}
              >
                开始写日记
              </Button>
            )}
          </Empty>
        ) : (
          <List
            dataSource={filteredPosts}
            renderItem={post => (
              <List.Item className="post-item">
                <Card 
                  className="post-card"
                  hoverable
                  onClick={() => handleViewPost(post)}
                >
                  <div className="post-card-content">
                    <div className="post-header">
                      <h3 className="post-title">{post.title}</h3>
                      <span className="post-date">
                        <CalendarOutlined />
                        {post.createdAt}
                      </span>
                    </div>
                    
                    <div className="post-preview">
                      {post.content.substring(0, 100)}
                      {post.content.length > 100 ? '...' : ''}
                    </div>
                    
                    <div className="post-footer">
                      {post.tags && post.tags.length > 0 && (
                        <div className="post-tags">
                          {post.tags.map(tag => (
                            <Tag key={tag} className="post-list-tag">{tag}</Tag>
                          ))}
                        </div>
                      )}
                      
                      <Space className="post-actions">
                        <Button 
                          icon={<EyeOutlined />}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPost(post);
                          }}
                        >
                          查看
                        </Button>
                        <Button 
                          icon={<EditOutlined />}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditPost(post);
                          }}
                        >
                          编辑
                        </Button>
                        <Button 
                          icon={<DeleteOutlined />}
                          size="small"
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                        >
                          删除
                        </Button>
                      </Space>
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* 删除确认模态框 */}
      <Modal
        title="确认删除"
        open={isDeleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="删除"
        cancelText="取消"
        okType="danger"
      >
        <p>确定要删除这篇日记吗？删除后无法恢复。</p>
      </Modal>
    </div>
  );
};

export default Archive;